import { Platform } from 'react-native';
import { File, Paths } from 'expo-file-system';
import { getAPIBaseURL } from '../api-url';
import { appFetch } from '../app-auth';
import type { VoiceUsageSnapshot } from '../../hooks/useVoiceUsage';
import { discardVoiceFile } from './voice-files';

export type VoiceRole = 'seeker' | 'employer';
export type VoiceLanguage = 'en' | 'ar' | 'auto';

export type VoiceErrorKind =
  | 'auth'
  | 'rate_limit'
  | 'bad_request'
  | 'unavailable'
  | 'timeout'
  | 'network'
  | 'cap_exceeded'
  | 'unknown';

export interface VoiceApiError {
  ok: false;
  kind: VoiceErrorKind;
  message: string;
  usage?: VoiceUsageSnapshot;
}

export interface TranscribeResult {
  ok: true;
  transcript: string;
  language: string;
  confidence: number;
  durationMs: number | null;
  transcriptId: string;
}

export interface SynthesizeResult {
  ok: true;
  fileUri: string;
  byteLength: number;
  transcriptId: string | null;
}

export interface TranscribeArgs {
  uri: string;
  mimeType?: string;
  role: VoiceRole;
  language?: VoiceLanguage;
  sessionId?: string;
  /**
   * Delete the native recording once the request has settled (retries
   * included). Only for takes the caller owns outright (Live's fresh file per
   * take): the composer reuses one file, which a newer take may already fill.
   */
  deleteAfterUpload?: boolean;
}

export interface SynthesizeArgs {
  text: string;
  role: VoiceRole;
  language?: 'en' | 'ar';
  sessionId?: string;
}

const VOICE_ERROR_CODE_TO_KIND: Record<string, VoiceErrorKind> = {
  VOICE_PROVIDER_AUTH: 'auth',
  VOICE_PROVIDER_RATE_LIMITED: 'rate_limit',
  VOICE_PROVIDER_BAD_REQUEST: 'bad_request',
  VOICE_PROVIDER_UNAVAILABLE: 'unavailable',
  VOICE_PROVIDER_TIMEOUT: 'timeout',
  VOICE_CAP_EXCEEDED: 'cap_exceeded',
  VOICE_AUDIO_MISSING: 'bad_request',
  VOICE_AUDIO_UNSUPPORTED: 'bad_request',
  VOICE_AUDIO_TOO_LARGE: 'bad_request',
  VALIDATION_ERROR: 'bad_request',
};

function classifyVoiceError(code?: string | null): VoiceErrorKind {
  if (!code) return 'unknown';
  return VOICE_ERROR_CODE_TO_KIND[code] ?? 'unknown';
}

/**
 * In a browser the recorder hands back a `blob:` URL (webm in Chromium and
 * Firefox, mp4 in Safari). The part's type must be the bare mime: the API's
 * allow-list has no entry for `audio/webm;codecs=opus`.
 */
async function readWebRecording(uri: string): Promise<{ blob: Blob; name: string } | null> {
  try {
    const source = await (await fetch(uri)).blob();
    const mime = source.type.split(';')[0]?.trim() || 'audio/webm';
    const extension = mime.includes('mp4') || mime.includes('m4a') ? 'm4a' : mime.split('/')[1] ?? 'webm';
    return { blob: new Blob([source], { type: mime }), name: `audio.${extension}` };
  } catch {
    return null;
  } finally {
    URL.revokeObjectURL(uri);
  }
}

export async function transcribeVoiceAudio(
  args: TranscribeArgs,
): Promise<TranscribeResult | VoiceApiError> {
  try {
    return await uploadForTranscript(args);
  } finally {
    // Web recordings are blob URLs, revoked by readWebRecording.
    if (args.deleteAfterUpload && Platform.OS !== 'web') discardVoiceFile(args.uri);
  }
}

async function uploadForTranscript(args: TranscribeArgs): Promise<TranscribeResult | VoiceApiError> {
  const form = new FormData();
  form.append('role', args.role);
  form.append('language', args.language ?? 'auto');
  if (args.sessionId) form.append('sessionId', args.sessionId);
  if (Platform.OS === 'web') {
    const audio = await readWebRecording(args.uri);
    if (!audio) {
      return { ok: false, kind: 'bad_request', message: 'Recording could not be read' };
    }
    form.append('file', audio.blob, audio.name);
  } else {
    form.append('file', {
      uri: args.uri,
      name: 'audio.m4a',
      type: args.mimeType ?? 'audio/m4a',
    } as unknown as Blob);
  }

  let response: Response;
  try {
    response = await appFetch(`${getAPIBaseURL()}/api/voice/transcribe`, {
      method: 'POST',
      body: form,
    });
  } catch (error) {
    return {
      ok: false,
      kind: 'network',
      message: (error as Error).message || 'Network error',
    };
  }

  const json = await response.json().catch(() => null);
  if (!response.ok || !json?.data) {
    const code: string | undefined = json?.error?.code;
    return {
      ok: false,
      kind: classifyVoiceError(code),
      message: json?.error?.message ?? `HTTP ${response.status}`,
      usage: json?.error?.usage,
    };
  }
  return {
    ok: true,
    transcript: json.data.transcript,
    language: json.data.language,
    confidence: json.data.confidence,
    durationMs: json.data.durationMs ?? null,
    transcriptId: json.data.transcriptId,
  };
}

export async function streamSynthesizeVoiceReply(
  args: SynthesizeArgs,
): Promise<SynthesizeResult | VoiceApiError> {
  let response: Response;
  try {
    response = await appFetch(`${getAPIBaseURL()}/api/voice/synthesize-stream`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        text: args.text,
        role: args.role,
        language: args.language,
        sessionId: args.sessionId,
      }),
    });
  } catch (error) {
    return { ok: false, kind: 'network', message: (error as Error).message || 'Network error' };
  }

  if (!response.ok) {
    const json = await response.json().catch(() => null);
    return {
      ok: false,
      kind: classifyVoiceError(json?.error?.code),
      message: json?.error?.message ?? `HTTP ${response.status}`,
      usage: json?.error?.usage,
    };
  }

  // React Native fetch does not expose response.body as a ReadableStream.
  // Read the full SSE payload as text and parse the events from it.
  let sseText: string;
  try {
    sseText = await response.text();
  } catch (err) {
    return { ok: false, kind: 'unknown', message: (err as Error).message || 'SSE stream unavailable' };
  }

  const chunks: Uint8Array[] = [];
  let transcriptId: string | null = null;
  let mime: string | null = null;

  for (const line of sseText.split('\n')) {
    if (!line.startsWith('data: ')) continue;
    let event: Record<string, unknown>;
    try {
      event = JSON.parse(line.slice(6)) as Record<string, unknown>;
    } catch {
      continue;
    }
    if (event.type === 'chunk' && typeof event.data === 'string') {
      chunks.push(base64ToUint8Array(event.data));
    } else if (event.type === 'done') {
      transcriptId = typeof event.transcriptId === 'string' ? event.transcriptId : null;
      mime = typeof event.mime === 'string' ? event.mime : null;
    } else if (event.type === 'error') {
      return {
        ok: false,
        kind: classifyVoiceError(typeof event.code === 'string' ? event.code : null),
        message: typeof event.message === 'string' ? event.message : 'Stream error',
      };
    }
  }

  const totalBytes = chunks.reduce((sum, c) => sum + c.length, 0);
  const combined = new Uint8Array(totalBytes);
  let offset = 0;
  for (const chunk of chunks) {
    combined.set(chunk, offset);
    offset += chunk.length;
  }

  const fileUri = saveVoiceAudio(combined, mime);
  return { ok: true, fileUri, byteLength: combined.byteLength, transcriptId };
}

/** Makes each saved clip's name unique (see saveVoiceAudio). */
let clipSerial = 0;

/**
 * Put synthesized speech where the player can open it: a cache file on
 * native (named for its real format — Nova's voice is WAV now, and players
 * trust the extension), a blob URL on web, which has no file cache.
 */
export function saveVoiceAudio(bytes: Uint8Array, mime: string | null): string {
  const isWav =
    (mime ?? '').includes('wav') ||
    (bytes.length >= 4 && bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46);
  const type = isWav ? 'audio/wav' : 'audio/mpeg';
  if (Platform.OS === 'web') {
    return URL.createObjectURL(new Blob([bytes as BlobPart], { type }));
  }
  // Unique per clip: two clips saved in the same millisecond used to share a
  // name, and the second deleted the first while it was still queued.
  clipSerial += 1;
  const name = `voice-tts-${Date.now().toString(36)}-${clipSerial.toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
  const file = new File(Paths.cache, `${name}.${isWav ? 'wav' : 'mp3'}`);
  if (file.exists) file.delete();
  file.create();
  file.write(bytes);
  return file.uri;
}

export function base64ToUint8Array(b64: string): Uint8Array {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

export async function synthesizeVoiceReply(
  args: SynthesizeArgs,
): Promise<SynthesizeResult | VoiceApiError> {
  let response: Response;
  try {
    response = await appFetch(`${getAPIBaseURL()}/api/voice/synthesize`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'audio/*' },
      body: JSON.stringify({
        text: args.text,
        role: args.role,
        language: args.language,
        sessionId: args.sessionId,
      }),
    });
  } catch (error) {
    return {
      ok: false,
      kind: 'network',
      message: (error as Error).message || 'Network error',
    };
  }

  if (!response.ok) {
    const json = await response.json().catch(() => null);
    const code: string | undefined = json?.error?.code;
    return {
      ok: false,
      kind: classifyVoiceError(code),
      message: json?.error?.message ?? `HTTP ${response.status}`,
      usage: json?.error?.usage,
    };
  }

  const transcriptId = response.headers.get('X-Voice-Transcript-Id');
  const arrayBuffer = await response.arrayBuffer();
  const bytes = new Uint8Array(arrayBuffer);
  return {
    ok: true,
    fileUri: saveVoiceAudio(bytes, response.headers.get('Content-Type')),
    byteLength: bytes.byteLength,
    transcriptId,
  };
}

export interface VoiceChatTurn {
  role: 'user' | 'assistant';
  content: string;
}

export interface VoiceChatArgs {
  message: string;
  history: VoiceChatTurn[];
  role: VoiceRole;
  language?: 'en' | 'ar' | 'mixed';
  sessionId?: string;
  threadId?: string;
}

export interface VoiceChatResult {
  ok: true;
  reply: string;
  summarized: boolean;
}

export async function voiceChat(
  args: VoiceChatArgs,
): Promise<VoiceChatResult | VoiceApiError> {
  let response: Response;
  try {
    response = await appFetch(`${getAPIBaseURL()}/api/voice/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        message: args.message,
        history: args.history,
        role: args.role,
        language: args.language,
        sessionId: args.sessionId,
        threadId: args.threadId,
      }),
    });
  } catch (error) {
    return {
      ok: false,
      kind: 'network',
      message: (error as Error).message || 'Network error',
    };
  }

  const json = await response.json().catch(() => null);
  if (!response.ok || !json?.data) {
    return {
      ok: false,
      kind: classifyVoiceError(json?.error?.code),
      message: json?.error?.message ?? `HTTP ${response.status}`,
    };
  }
  return {
    ok: true,
    reply: json.data.reply,
    summarized: !!json.data.summarized,
  };
}

export interface VoiceThreadHistoryResult {
  ok: true;
  history: VoiceChatTurn[];
}

export async function fetchVoiceThreadHistory(
  threadId: string,
): Promise<VoiceThreadHistoryResult | VoiceApiError> {
  try {
    const response = await appFetch(
      `${getAPIBaseURL()}/api/voice/thread-history/${encodeURIComponent(threadId)}`,
      { method: 'GET', headers: { 'Content-Type': 'application/json' } },
    );
    const json = await response.json().catch(() => null);
    if (!response.ok || !json?.data) {
      return {
        ok: false,
        kind: classifyVoiceError(json?.error?.code),
        message: json?.error?.message ?? `HTTP ${response.status}`,
      };
    }
    return {
      ok: true,
      history: Array.isArray(json.data.history) ? (json.data.history as VoiceChatTurn[]) : [],
    };
  } catch (error) {
    return {
      ok: false,
      kind: 'network',
      message: (error as Error).message || 'Network error',
    };
  }
}

export async function softDeleteVoiceTranscript(
  transcriptId: string,
): Promise<{ ok: true } | VoiceApiError> {
  try {
    const response = await appFetch(
      `${getAPIBaseURL()}/api/voice/transcripts/${transcriptId}/delete`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' } },
    );
    if (!response.ok) {
      return {
        ok: false,
        kind: 'unknown',
        message: `HTTP ${response.status}`,
      };
    }
    return { ok: true };
  } catch (error) {
    return {
      ok: false,
      kind: 'network',
      message: (error as Error).message || 'Network error',
    };
  }
}
